import { useCallback, useEffect, useMemo, useState } from 'react';
import Hammer from 'react-hammerjs';
import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'hammerjs';
import { JSONContent } from '@tiptap/core';

import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import {
  InteractiveResponseAnswerIcons,
  InteractiveResponseItemCount,
  InteractiveResponseTitle,
} from '../../shared/interactive-response-shared';
import {
  InteractiveCategoriseSmallAttempt,
  RESPONSE_FREE,
  ViewProps,
} from '../interactive-response-categorise.constants';
import { useTileScroll } from '../../shared/interactive-response-shared.hooks';
import {
  DragId,
  INTERACTIVE_CONTAINER_ID,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import {
  ChangeAnswerSmall,
  CheckAnswerSmall,
} from '../../shared/interactive-response-check-answer';
import {
  initialiseDragItems,
  populateAnswersSmall,
} from './interactive-response-categorise-play.utils';
import {
  InteractiveDragItemButtonContainer,
  InteractiveItemImage,
  InteractiveTextImageContainer,
} from '../../../shared/interactive-drag-drop/interactive-drag.style';
import {
  DragItemSmall,
  DropItem,
} from './interactive-response-categorise-small.constants';
import {
  checkAnswer,
  checkAnswers,
  getResults,
  resetIncorrectItemsSmall,
} from './interactive-response-categorise-small.utils';
import {
  InteractiveCanvas,
  InteractiveResponseModalControlsWrapper,
  InteractiveResponsePlayContainer,
  InteractiveResponseScrollableList,
  InteractiveResponseScrollableWrapper,
} from '../../shared/interactive-response-shared.style';
import * as Styled from './interactive-response-categorise-small.style';
import {
  InteractiveResponseHelpControls,
  InteractiveResponseNavMobileControls,
} from '../../shared/interactive-response-controls';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import { isTextImageVariant } from '../../shared/interactive-response-shared.utils';
import {
  ACTIVITIES_RESPONSE_VARIANT_TEXT,
  InquisitiveActivitiesResponseVariant,
} from 'global/constants';
import InteractiveResponseRichtextView from '../../shared/interactive-response-richtext-view';
import InteractiveResponseAudioButton from '../../shared/interactive-response-audio-button';
import {
  InteractiveAudioProvider,
  useInteractiveAudioContext,
} from '../../shared/interactive-audio.context';
import { ImageViewWrapper } from 'approot/activity-builder/components/image/image.style';
import { ComponentImageContainer } from 'approot/activity-builder/components/component-image.style';

type Props = ViewProps & {
  playAudio: boolean;
  attemptAnswers?: InteractiveResponseAnswerFragment;
  onClose: () => void;
  playStart: () => void;
  playClick: () => void;
  toggleAudio: () => void;
  onAnswerAttempt: InteractiveCategoriseSmallAttempt;
  showAnswer: boolean;
};

const InteractiveResponseCategoriseSmall = ({
  properties,
  playStart,
  playClick,
  playAudio,
  attemptAnswers,
  onAnswerAttempt,
  toggleAudio,
  showAnswer,
}: Props) => {
  const [dropItems, setDropItems] = useState<DropItem[]>([]);
  const [dragItems, setDragItems] = useState<DragItemSmall[]>([]);
  const [scrollRef, setScrollRef] = useState<HTMLElement | null>(null);
  const [hasCheckedAnswer, setHasCheckedAnswer] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [playAttempt, setPlayAttempt] = useState<number>(1);

  const categoriseProperties = properties.interactiveResponseCategorise;

  const isCheckAnswerAtEnd = categoriseProperties.checkAnswerAtEnd;
  const isFreeResponse = categoriseProperties.answerType === RESPONSE_FREE;

  const canShowAnswer = showAnswer && !isFreeResponse;

  const { setAudioList } = useInteractiveAudioContext();

  const {
    currentSlide,
    prevDisabled,
    nextDisabled,
    handleNext,
    handlePrev,
    reset,
  } = useTileScroll({
    containerRef: scrollRef,
    slideCount: categoriseProperties.items.length,
  });

  const initialiseItems = useCallback(
    (startAgain?: boolean) => {
      if (attemptAnswers && !startAgain) {
        const { dragItems, dropItems } = populateAnswersSmall({
          attemptedAnswers: attemptAnswers,
          items: categoriseProperties.items,
          categories: categoriseProperties.categories,
          variant: categoriseProperties.variant,
          showAnswer: canShowAnswer,
        });

        setDropItems(dropItems);
        setDragItems(dragItems);
        setHasCheckedAnswer(true);
      } else {
        setDropItems(
          categoriseProperties.categories.map(category => ({
            id: category.id,
            label: category.label,
            dragItems: {},
          }))
        );
        setDragItems(
          initialiseDragItems({
            items: categoriseProperties.items,
            shouldShuffle: true,
            variant: categoriseProperties.variant,
          })
        );
        setHasCheckedAnswer(false);
      }

      setPlayAttempt(playAttempt + 1);
      setIsActive(true);
      reset();
    },
    [
      playAttempt,
      categoriseProperties.items,
      categoriseProperties.categories,
      categoriseProperties.variant,
      setPlayAttempt,
      setHasCheckedAnswer,
      setIsActive,
      reset,
      attemptAnswers,
      canShowAnswer,
    ]
  );

  const setup = useCallback(
    (startAgain?: boolean) => {
      setTimeout(() => {
        initialiseItems(startAgain);
        playStart();
      }, 100);
    },
    [initialiseItems, playStart]
  );

  const tryAgain = useCallback(() => {
    const { dragItems: updatedDragItems, dropItems: updatedDropItems } =
      resetIncorrectItemsSmall(dragItems, dropItems);

    setDragItems(updatedDragItems);
    setDropItems(updatedDropItems);
    setHasCheckedAnswer(false);

    setTimeout(() => {
      playStart();
    }, 100);
  }, [
    dragItems,
    dropItems,
    setDragItems,
    setDropItems,
    setHasCheckedAnswer,
    playStart,
  ]);

  const isEverythingAnswered = useMemo(() => {
    return dragItems.every(di => di.isDropped);
  }, [dragItems]);

  const { isEverythingCorrect, result } = useMemo(
    () => getResults(dragItems),
    [dragItems]
  );

  useEffect(() => {
    setup();
    //eslint-disable-next-line
  }, [properties, attemptAnswers]);

  useEffect(() => {
    setAudioList(
      categoriseProperties.items.map(item => ({
        itemId: item.itemId,
        audioUrl: item.audio?.original,
      }))
    );
  }, [setAudioList, categoriseProperties.items]);

  return (
    <InteractiveResponsePlayContainer>
      <InteractiveCanvas id={INTERACTIVE_CONTAINER_ID}>
        <InteractiveResponseHelpControls
          howToPlay={categoriseProperties.howToPlay}
          toggleAudio={toggleAudio}
          playAudio={playAudio}
        />
        <InteractiveResponseTitle
          title={categoriseProperties.title}
          subTitle={categoriseProperties.subTitle}
        />
        <Hammer
          options={{
            recognizers: {
              swipe: { enable: true },
            },
          }}
          onSwipe={arg => {
            if (arg.direction === DIRECTION_LEFT && !prevDisabled) {
              handlePrev();
            } else if (arg.direction === DIRECTION_RIGHT && !nextDisabled) {
              handleNext();
            }
          }}
        >
          <InteractiveResponseScrollableWrapper>
            <InteractiveResponseScrollableList ref={setScrollRef}>
              {dragItems.map((dragItem, tileIndex) => (
                <Styled.InteractiveResponseCategoriseTileLi
                  id={`${dragItem.id}`}
                  $isInFocus={currentSlide === tileIndex}
                  key={`${playAttempt}-${dragItem.id}-${tileIndex}-${playAudio}`}
                >
                  <InteractiveResponseCategoriseTileSmall
                    id={dragItem.id}
                    isInFocus={currentSlide === tileIndex}
                    isCorrect={
                      currentSlide !== tileIndex || !canShowAnswer
                        ? undefined
                        : dragItem.isCorrect
                    }
                    imageUrl={dragItem.question.image?.small}
                    isFit={dragItem.question.image?.isFit}
                    description={dragItem.question.image?.description || ''}
                    variant={dragItem.question.variant}
                    text={dragItem.question.text || null}
                    richtext={dragItem.question.richtext || null}
                    audioUrl={dragItem.question.audioUrl}
                    baseColour={dragItem.question.baseColour}
                  />
                </Styled.InteractiveResponseCategoriseTileLi>
              ))}
            </InteractiveResponseScrollableList>
          </InteractiveResponseScrollableWrapper>
        </Hammer>

        <InteractiveResponseItemCount
          index={currentSlide}
          total={categoriseProperties.items.length}
        />

        <Styled.InteractiveResponsCategoriseOptions>
          {dropItems.map(dropItem => {
            return (
              <li key={`${playAttempt}-${dropItem.id}-${playAudio}`}>
                <Styled.InteractiveResponseCategoriseOptionItem
                  type="button"
                  $isAnswered={
                    !!dropItem.dragItems[dragItems[currentSlide]?.id] ||
                    dropItem.dragItems[dropItem.id]?.isAnswered
                  }
                  $isCorrect={
                    !canShowAnswer
                      ? undefined
                      : dropItem.dragItems[dragItems[currentSlide]?.id]
                          ?.isCorrect
                  }
                  disabled={
                    !canShowAnswer && dragItems.every(di => di.isDropped)
                  }
                  onClick={() => {
                    const dragItem = dragItems[currentSlide];

                    // remove if added to other area first
                    dropItems.forEach(di => {
                      delete di.dragItems[dragItem.id];
                    });

                    dragItem.isDropped = true;
                    dropItem.dragItems[dragItem.id] = {};

                    const isFinalAnswer = dragItems.every(di => di.isDropped);

                    if (canShowAnswer && !isCheckAnswerAtEnd) {
                      checkAnswer(dropItem, dragItem);
                    }

                    if (isFinalAnswer && !isCheckAnswerAtEnd) {
                      onAnswerAttempt(dropItems);
                    }

                    playClick();

                    setDropItems(() => [...dropItems]);
                    setDragItems(() => [...dragItems]);
                  }}
                >
                  {dropItem.label}
                </Styled.InteractiveResponseCategoriseOptionItem>
              </li>
            );
          })}
        </Styled.InteractiveResponsCategoriseOptions>

        {canShowAnswer &&
          isEverythingAnswered &&
          (hasCheckedAnswer || !isCheckAnswerAtEnd) && (
            <CheckAnswerSmall
              isActive={isActive}
              setIsActive={setIsActive}
              result={result}
              isEverythingCorrect={isEverythingCorrect}
              moreInformation={categoriseProperties.moreInformation}
              onStartOver={() => {
                setup(true);
              }}
              onTryAgain={tryAgain}
            />
          )}

        {!canShowAnswer && isEverythingAnswered && hasCheckedAnswer && (
          <ChangeAnswerSmall
            isActive={isActive}
            setIsActive={setIsActive}
            moreInformation={
              properties.interactiveResponseCategorise.moreInformation
            }
            onStartOver={() => {
              setup(true);
            }}
          />
        )}
      </InteractiveCanvas>
      <InteractiveResponseModalControlsWrapper>
        <InteractiveResponseNavMobileControls
          onNext={handleNext}
          onPrevious={handlePrev}
          onCheckAnswer={() => {
            const { dragItems: updatedDragItems, dropItems: updatedDropItems } =
              checkAnswers(dropItems, dragItems);
            setHasCheckedAnswer(true);
            setDropItems(() => [...updatedDropItems]);
            setDragItems(() => [...updatedDragItems]);
            setIsActive(true);
            onAnswerAttempt(updatedDropItems);
          }}
          buttonLabel={canShowAnswer ? 'Submit Answer' : 'Check Answer'}
          canCheckAnswer={
            isCheckAnswerAtEnd && isEverythingAnswered && !hasCheckedAnswer
          }
          isNextDisabled={nextDisabled}
          isPrevDisabled={prevDisabled}
        />
      </InteractiveResponseModalControlsWrapper>
    </InteractiveResponsePlayContainer>
  );
};

const InteractiveResponseCategoriseTileSmall = ({
  id,
  imageUrl,
  isFit,
  description,
  isCorrect,
  isInFocus,
  variant,
  text,
  richtext,
  audioUrl,
  baseColour,
}: {
  id: DragId;
  description: string;
  imageUrl?: string | null;
  isFit?: boolean;
  isCorrect?: boolean;
  isInFocus: boolean;
  baseColour: string | null;
  variant?: InquisitiveActivitiesResponseVariant;
  text: string | null;
  richtext: JSONContent | null;
  audioUrl?: string | null;
}) => {
  return (
    <Styled.InteractiveResponseCategoriseTile
      id={`interactive-drag-${id}`}
      tabIndex={0}
      role="button"
      $isInFocus={isInFocus}
      $isCorrect={isCorrect}
      $baseColour={baseColour}
    >
      <InteractiveResponseAnswerIcons isCorrect={isCorrect} />
      <Styled.InteractiveResponseCategoriseTileInner>
        {variant === ACTIVITIES_RESPONSE_VARIANT_TEXT ? (
          <InteractiveResponseRichtextView
            baseColour={baseColour}
            richtext={richtext}
          />
        ) : (
          <>
            {isFit && imageUrl ? (
              <ImageViewWrapper>
                <ComponentImageContainer $imageHasLoaded>
                  <img src={imageUrl} alt="" />
                </ComponentImageContainer>
              </ImageViewWrapper>
            ) : imageUrl ? (
              <InteractiveItemImage
                $imageUrl={imageUrl}
                $imageHasLoaded
                role="image"
              />
            ) : null}

            {isTextImageVariant(variant) && text && (
              <InteractiveTextImageContainer>
                {text}
              </InteractiveTextImageContainer>
            )}
          </>
        )}
        <ScreenReaderOnly>{description}</ScreenReaderOnly>

        {audioUrl && (
          <InteractiveDragItemButtonContainer>
            <InteractiveResponseAudioButton
              itemId={id}
              description={description}
            />
          </InteractiveDragItemButtonContainer>
        )}
      </Styled.InteractiveResponseCategoriseTileInner>
    </Styled.InteractiveResponseCategoriseTile>
  );
};

const InteractiveResponseCategoriseSmallWrapper = (props: Props) => {
  return (
    <InteractiveAudioProvider>
      <InteractiveResponseCategoriseSmall {...props} />
    </InteractiveAudioProvider>
  );
};

export default InteractiveResponseCategoriseSmallWrapper;
